import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import useTranslation from "next-translate/useTranslation";
import PhoneLink from "@components/utils/phone-link";
import { directionsUrl, fullAddress } from "@lib/utils/geo";
import * as gtag from "@lib/utilities/analytics";

/**
 * Replaces the interactive Google Maps component (D48).
 *
 * The old component loaded the Maps JavaScript SDK on the contact page. That
 * has the same problem an embedded Maps iframe has, and the same problem the
 * runtime Google Fonts link had: it contacts Google and sets cookies on page
 * load, before the visitor has answered the consent banner. Building a consent
 * stack while a map quietly opts everyone in would be self-defeating.
 *
 * So the imagery is a LOCAL asset (owner-supplied export, 2026-09-07) and the
 * only thing that reaches Google is a click the visitor chose to make.
 *
 * Pan and zoom are approximated with a LIGHTBOX: the inline image is a
 * readable crop around the clinic; clicking it opens the full-area export at
 * higher resolution. Esc, backdrop click or the ✕ close it. For real
 * navigation the «Άνοιγμα στους Χάρτες Google» link remains the door.
 */
export interface StaticMapProps {
  /** local map image in /public — never a Google-hosted URL */
  src?: string;
  /** larger image for the click-to-zoom lightbox; omit to disable zoom */
  zoomSrc?: string;
  addressLine: string;
  locality: string;
  postalCode?: string;
  googleMapsUrl: string;
  /** "Άνοιγμα στους Χάρτες Google" */
  openLabel: string;
  className?: string;
  /** true when the map sits above the fold (the contact page leads with it) —
   *  a lazy-loaded LCP image is Lighthouse's cardinal sin */
  priority?: boolean;
  /** full-width placements crop cinematic (21:9-ish) — the native 10:7 map
   *  at content width would tower ~760px; the pin is centred, so cropping is
   *  safe (owner restructure, 2026-09-10) */
  wide?: boolean;
}

export default function StaticMap({
  src = "/images/map-korydallos-tall.jpg",
  zoomSrc = "/images/map-korydallos-large.jpg",
  addressLine,
  locality,
  postalCode,
  googleMapsUrl,
  openLabel,
  className = "",
  priority = false,
  wide = false,
}: StaticMapProps) {
  const { t } = useTranslation("main");
  const [zoomed, setZoomed] = useState(false);
  // ONE map_zoom per pageview (owner, P11-d): a visitor opening and closing
  // the lightbox five times is one curious visitor, not five signals — the
  // ref latches after the first open and remounts fresh on the next page
  const zoomCounted = useRef(false);
  const openZoom = useCallback(() => {
    setZoomed(true);
    if (!zoomCounted.current) {
      zoomCounted.current = true;
      gtag.event("map_zoom", { event_category: "engagement", event_label: wide ? "map-section" : "static-map" });
    }
  }, [wide]);
  const close = useCallback(() => setZoomed(false), []);

  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [zoomed, close]);

  const alt = t("map.alt", { address: `${addressLine}, ${locality}` });

  return (
    <>
      <figure className={`relative overflow-hidden rounded-xl border border-rule ${className}`}>
        <button
          type="button"
          onClick={zoomSrc ? openZoom : undefined}
          aria-label={zoomSrc ? t("map.zoomAria", { alt }) : alt}
          className={`block w-full ${wide ? "relative aspect-[16/9] md:aspect-[21/9]" : ""} ${zoomSrc ? "cursor-zoom-in" : "cursor-default"}`}
        >
          <Image
            src={src}
            alt={alt}
            width={1200}
            height={840}
            // muted toward the site's grayscale register (owner, 2026-09-07):
            // raw satellite colour shouted next to the clinical photography.
            // Not full grayscale — street/label legibility survives at 30%.
            className={`${wide ? "absolute inset-0 h-full" : "h-auto"} w-full object-cover [filter:saturate(0.3)_contrast(1.05)]`}
            sizes={wide ? "(min-width: 1140px) 68rem, 95vw" : "(min-width: 1024px) 40rem, 95vw"}
            priority={priority}
          />
          {/* brand marker over the (now muted) Google pin — the inline crop
              is centred on the clinic, so the anchor is the exact centre */}
          <span aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full">
            <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="#6600cc"
              strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 10.5c0 5.2-8 11-8 11s-8-5.8-8-11a8 8 0 1 1 16 0Z" fill="#ffffff" />
              <circle cx="12" cy="10.2" r="2.8" fill="#6600cc" stroke="none" />
            </svg>
          </span>
        </button>
        {/* the address is NOT repeated here (owner, 2026-09-07) — the info
            column beside the map carries it, linked; the caption bar is just
            the door to the live listing */}
        <figcaption className="absolute bottom-3 right-3">
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            // «locate»: opening the live listing to SEE where the clinic is —
            // a distinct intent from asking for directions (owner, P11-d)
            onClick={() => gtag.event("map_locate_click", { event_category: "engagement", event_label: "map-caption" })}
            className="rounded-full bg-violet-700 px-4 py-1.5 text-xs font-extrabold text-white shadow-md transition-colors hover:bg-violet-800"
          >
            {openLabel}
          </a>
        </figcaption>
      </figure>

      {zoomed && zoomSrc ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          className="fixed inset-0 z-50 grid place-items-center bg-ink/80 p-4"
          onClick={close}
        >
          <button
            type="button"
            aria-label={t("common.close")}
            onClick={close}
            className="absolute right-5 top-5 grid h-11 w-11 cursor-pointer place-items-center rounded-full bg-white text-violet-700"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
          {/* plain img: the lightbox wants the file verbatim, no optimizer pass */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={zoomSrc}
            alt={alt}
            className="max-h-[82vh] max-w-full rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {/* the zoomed view keeps the actions in reach (owner, 2026-09-07):
              a visitor studying the map is deciding whether to come — call,
              open the live listing, or route from where they stand */}
          <div
            className="absolute inset-x-0 bottom-5 flex flex-wrap items-center justify-center gap-3 px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <PhoneLink variant="primary" placement="map-lightbox" />
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => gtag.event("map_locate_click", { event_category: "engagement", event_label: "map-lightbox" })}
              className="rounded-full bg-white px-5 py-3 font-extrabold text-violet-700 shadow-md transition-colors hover:bg-violet-050"
            >
              {openLabel}
            </a>
            <a
              href={directionsUrl(fullAddress({ addressStreet: addressLine, addressLocality: locality, addressPostalCode: postalCode }))}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => gtag.event("map_directions_click", { event_category: "engagement", event_label: "static-map" })}
              className="rounded-full bg-white px-5 py-3 font-extrabold text-violet-700 shadow-md transition-colors hover:bg-violet-050"
            >
              {t("map.directionsShort")}
            </a>
          </div>
        </div>
      ) : null}
    </>
  );
}
